import {
  INTRANET_PAGE_SIZE,
  intranetAppearanceSchema,
  type UpdateIntranetAppearanceRequest,
  intranetCategoryListResponseSchema,
  intranetCategoryMembersOverviewSchema,
  intranetCategorySchema,
  intranetContentDetailSchema,
  intranetContentListResponseSchema,
  intranetPostingAuthorsSchema,
  intranetDepartmentOptionsResponseSchema,
  roleOptionListSchema,
  type CreateIntranetCategoryRequest,
  type CreateIntranetContentRequest,
  type IntranetContentKind,
  type IntranetSection,
  type UpdateIntranetContentRequest,
  type UpdateIntranetCategoryRequest,
  type PinIntranetContentRequest,
} from '@gb-ticket/shared-types';
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { z } from 'zod';

import { apiFetch } from '@/lib/api';
import { toSearchParams } from '@/lib/format';

const okSchema = z.object({ ok: z.literal(true) });

export const intranetQueryKey = ['intranet'] as const;
export const intranetContentsKey = [...intranetQueryKey, 'contents'] as const;
const contentsKey = intranetContentsKey;
const categoriesKey = [...intranetQueryKey, 'categories'] as const;
const categoryPosterRolesKey = [...categoriesKey, 'poster-roles'] as const;

export function intranetContentKey(slug: string | undefined) {
  return [...contentsKey, 'detail', slug] as const;
}

export type IntranetContentsFilter = {
  kind?: IntranetContentKind;
  section?: IntranetSection;
  categoryId?: string;
  /** Editorial view: drafts and every audience. */
  drafts?: boolean;
  /** With `drafts`: only drafts, only published, or only the archive. Omit for all three. */
  status?: 'draft' | 'published' | 'archived';
  /** Library entries whose review deadline has passed. */
  reviewDue?: boolean;
  /** Own bookmarks only. */
  saved?: boolean;
  /** `true` = pinned only, `false` = stream without pins. Omit for both. */
  pinned?: boolean;
  /** Editorial calendar, both dates or neither. */
  from?: string;
  to?: string;
  /** Active site. Omitted means the filter is cleared. */
  siteId?: string;
  page: number;
  pageSize?: number;
};

export function useIntranetContents(filter: IntranetContentsFilter, enabled = true) {
  return useQuery({
    queryKey: [...contentsKey, 'list', filter],
    enabled,
    queryFn: () =>
      apiFetch(
        `/intranet/contents${toSearchParams({
          kind: filter.kind,
          section: filter.section,
          categoryId: filter.categoryId,
          drafts: filter.drafts ? 'true' : undefined,
          status: filter.status,
          reviewDue: filter.reviewDue ? 'true' : undefined,
          saved: filter.saved ? 'true' : undefined,
          pinned: filter.pinned === undefined ? undefined : filter.pinned ? 'true' : 'false',
          from: filter.from,
          to: filter.to,
          siteId: filter.siteId,
          page: filter.page,
          pageSize: filter.pageSize,
        })}`,
        intranetContentListResponseSchema,
      ),
    placeholderData: keepPreviousData,
  });
}

export function useIntranetFeedStream(
  filter: Omit<IntranetContentsFilter, 'page' | 'pinned' | 'pageSize'>,
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: [...contentsKey, 'stream', filter],
    enabled,
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      apiFetch(
        `/intranet/contents${toSearchParams({
          kind: filter.kind,
          section: filter.section,
          categoryId: filter.categoryId,
          drafts: filter.drafts ? 'true' : undefined,
          status: filter.status,
          reviewDue: filter.reviewDue ? 'true' : undefined,
          saved: filter.saved ? 'true' : undefined,
          page: pageParam,
          pageSize: INTRANET_PAGE_SIZE,
        })}`,
        intranetContentListResponseSchema,
      ),
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.items.length, 0);
      if (lastPage.items.length === 0 || loaded >= lastPage.total) {
        return undefined;
      }
      return lastPage.page + 1;
    },
  });
}

export function useIntranetContent(slug: string | undefined) {
  return useQuery({
    queryKey: intranetContentKey(slug),
    queryFn: () =>
      apiFetch(`/intranet/contents/${encodeURIComponent(slug ?? '')}`, intranetContentDetailSchema),
    enabled: Boolean(slug),
  });
}

const appearanceKey = [...intranetQueryKey, 'appearance'] as const;

export function useIntranetAppearance(enabled = true) {
  return useQuery({
    queryKey: appearanceKey,
    queryFn: () => apiFetch('/intranet/appearance', intranetAppearanceSchema),
    enabled,
  });
}

export function useUpdateIntranetAppearance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateIntranetAppearanceRequest) =>
      apiFetch('/intranet/appearance', intranetAppearanceSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: appearanceKey }),
  });
}

export function useUploadIntranetAppearance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, alt }: { file: File; alt: string }) => {
      const body = new FormData();
      body.append('file', file);
      body.append('alt', alt);
      return apiFetch('/intranet/appearance/background', intranetAppearanceSchema, {
        method: 'POST',
        body,
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: appearanceKey }),
  });
}

export function useIntranetCategories() {
  return useQuery({
    queryKey: categoriesKey,
    queryFn: () => apiFetch('/intranet/categories', intranetCategoryListResponseSchema),
  });
}

export function useIntranetCategoryPosterRoles() {
  return useQuery({
    queryKey: categoryPosterRolesKey,
    queryFn: () => apiFetch('/intranet/categories/poster-roles', roleOptionListSchema),
  });
}

export function useIntranetDepartments(enabled: boolean) {
  return useQuery({
    queryKey: [...intranetQueryKey, 'departments'],
    queryFn: () => apiFetch('/intranet/departments', intranetDepartmentOptionsResponseSchema),
    enabled,
  });
}

function useInvalidateContents() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: contentsKey });
}

/** Signed-in person plus everyone they may publish as. One entry hides the composer choice. */
export function useIntranetPostingAuthors(enabled = true) {
  return useQuery({
    queryKey: [...intranetQueryKey, 'posting-authors'],
    enabled,
    queryFn: () => apiFetch('/intranet/author-grants/options', intranetPostingAuthorsSchema),
  });
}

export function useCreateIntranetContent() {
  const invalidate = useInvalidateContents();
  return useMutation({
    mutationFn: (body: CreateIntranetContentRequest) =>
      apiFetch('/intranet/contents', intranetContentDetailSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: invalidate,
  });
}

export function useUpdateIntranetContent() {
  const invalidate = useInvalidateContents();
  return useMutation({
    mutationFn: ({ slug, body }: { slug: string; body: UpdateIntranetContentRequest }) =>
      apiFetch(`/intranet/contents/${encodeURIComponent(slug)}`, intranetContentDetailSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: invalidate,
  });
}

export function useArchiveIntranetContent() {
  const invalidate = useInvalidateContents();
  return useMutation({
    mutationFn: (slug: string) =>
      apiFetch(
        `/intranet/contents/${encodeURIComponent(slug)}/archive`,
        intranetContentDetailSchema,
        { method: 'POST' },
      ),
    onSuccess: invalidate,
  });
}

export function useRestoreIntranetContent() {
  const invalidate = useInvalidateContents();
  return useMutation({
    mutationFn: (slug: string) =>
      apiFetch(
        `/intranet/contents/${encodeURIComponent(slug)}/restore`,
        intranetContentDetailSchema,
        { method: 'POST' },
      ),
    onSuccess: invalidate,
  });
}

export function useSetIntranetPublished() {
  const invalidate = useInvalidateContents();
  return useMutation({
    mutationFn: ({
      slug,
      published,
      notifyAudience,
    }: {
      slug: string;
      published: boolean;
      notifyAudience?: boolean;
    }) =>
      apiFetch(
        `/intranet/contents/${encodeURIComponent(slug)}/${published ? 'publish' : 'unpublish'}`,
        intranetContentDetailSchema,
        {
          method: 'POST',
          body: published
            ? JSON.stringify(notifyAudience === undefined ? {} : { notifyAudience })
            : undefined,
        },
      ),
    onSuccess: invalidate,
  });
}

export function useDeleteIntranetContent() {
  const invalidate = useInvalidateContents();
  return useMutation({
    mutationFn: (slug: string) =>
      apiFetch(`/intranet/contents/${encodeURIComponent(slug)}`, okSchema, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useUploadIntranetCover() {
  const invalidate = useInvalidateContents();
  return useMutation({
    mutationFn: ({ slug, file, alt }: { slug: string; file: File; alt: string }) => {
      const body = new FormData();
      body.append('file', file);
      body.append('alt', alt);
      return apiFetch(
        `/intranet/contents/${encodeURIComponent(slug)}/cover`,
        intranetContentDetailSchema,
        { method: 'POST', body },
      );
    },
    onSuccess: invalidate,
  });
}

export function useDeleteIntranetCover() {
  const invalidate = useInvalidateContents();
  return useMutation({
    mutationFn: (slug: string) =>
      apiFetch(
        `/intranet/contents/${encodeURIComponent(slug)}/cover`,
        intranetContentDetailSchema,
        { method: 'DELETE' },
      ),
    onSuccess: invalidate,
  });
}

/** One toggle for the editorial form and the editorial list. */
export function useSetIntranetPinned() {
  const invalidate = useInvalidateContents();
  return useMutation({
    mutationFn: ({
      slug,
      pinned,
      span,
      until,
    }: { slug: string; pinned: boolean } & Partial<PinIntranetContentRequest>) =>
      apiFetch(`/intranet/contents/${encodeURIComponent(slug)}/pin`, intranetContentDetailSchema, {
        method: pinned ? 'POST' : 'DELETE',
        body: pinned ? JSON.stringify({ span, until }) : undefined,
      }),
    onSuccess: invalidate,
  });
}

export function useCreateIntranetCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateIntranetCategoryRequest) =>
      apiFetch('/intranet/categories', intranetCategorySchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: categoriesKey }),
  });
}

export function useUpdateIntranetCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateIntranetCategoryRequest }) =>
      apiFetch(`/intranet/categories/${id}`, intranetCategorySchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: categoriesKey }),
  });
}

export function useJoinIntranetCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/intranet/categories/${id}/join`, okSchema, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: intranetQueryKey }),
  });
}

export function useLeaveIntranetCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/intranet/categories/${id}/join`, okSchema, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: intranetQueryKey }),
  });
}

export function useDeleteIntranetCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/intranet/categories/${id}`, okSchema, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: intranetQueryKey }),
  });
}

export function useUploadIntranetCategoryImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, kind, file }: { id: string; kind: 'image' | 'banner'; file: File }) => {
      const body = new FormData();
      body.append('file', file);
      body.append('alt', 'Channel');
      return apiFetch(`/intranet/categories/${id}/${kind}`, intranetCategorySchema, {
        method: 'POST',
        body,
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: categoriesKey }),
  });
}

export function useDeleteIntranetCategoryImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, kind }: { id: string; kind: 'image' | 'banner' }) =>
      apiFetch(`/intranet/categories/${id}/${kind}`, intranetCategorySchema, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: categoriesKey }),
  });
}

export function useIntranetCategoryMembers(categoryId: string | undefined) {
  return useQuery({
    queryKey: [...categoriesKey, 'members', categoryId],
    queryFn: () =>
      apiFetch(`/intranet/categories/${categoryId}/members`, intranetCategoryMembersOverviewSchema),
    enabled: Boolean(categoryId),
  });
}
